import * as THREE from 'three';
import { getPhotorealisticTilesGroup } from './photorealisticTilesRegistry';

/**
 * Photorealistic tile floor sampling — world Y from downward raycasts.
 * Docs: docs/ADU_SITE_PLANNER.md
 *
 * Elevation ASL is for ReorientationPlugin origin only. Floor seating always
 * comes from raycasts. Never apply ECEF-scale bounding boxes (Y ≈ ±4e6).
 */

/** Thin lift so the amber fill sits on top of the mesh, not buried. */
export const LOT_SURFACE_BIAS = 0.12;

/** Extruded lot pad thickness (meters). */
export const LOT_PAD_THICKNESS = 0.65;

/** Half-thickness — pad top sits this far above the locked floor. */
export const LOT_PAD_HALF = LOT_PAD_THICKNESS / 2;

/** Fixed iso eye (meters) relative to floor — same framing for every site load. */
export const FIXED_LOAD_EYE = [32, 24, 38];

/** Minimum pickable tile meshes near the pin before we trust a floor lock. */
export const MIN_MESHES_FOR_FLOOR_LOCK = 14;

/** Consecutive ray samples within this band before lock (meters). */
export const FLOOR_LOCK_STABLE_DELTA = 3.5;

/** How many consecutive stable ray samples are required to lock. */
export const FLOOR_LOCK_STABLE_COUNT = 3;

/**
 * Soft timeout before best-effort seating — only if meshes + a sample exist.
 * Never force-lock on empty tiles; the global site loader covers long waits.
 * Docs: docs/ADU_SITE_PLANNER.md
 */
export const FLOOR_LOCK_TIMEOUT_SEC = 45;

/**
 * Reject samples outside this band (meters, local scene after reorientation).
 * Must cover inhabited ASL when Elevation reorient failed (height: 0 → ground ≈ ASL).
 * Still rejects ECEF-scale garbage (~1e6).
 */
const MAX_ABS_FLOOR_Y = 8000;

const _raycaster = new THREE.Raycaster();
const _origin = new THREE.Vector3();
const _down = new THREE.Vector3(0, -1, 0);
const _box = new THREE.Box3();
const _center = new THREE.Vector3();
const _parentWorld = new THREE.Vector3();

let cachedOriginFloorY = null;
let lastDebug = {
  meshCount: 0,
  sampleY: null,
  source: 'none',
  at: [0, 0],
};
let settleGeneration = 0;

export function getFloorDebug() {
  return { ...lastDebug, cachedOriginFloorY, settleGeneration };
}

/** True when we have enough LOD meshes and a plausible downward ray hit. */
export function isFloorReadyForLock(debug = lastDebug) {
  if (!debug) return false;
  if ((debug.meshCount || 0) < MIN_MESHES_FOR_FLOOR_LOCK) return false;
  // Mesh AABB alone is too easy to lock early under the real surface.
  if (debug.source !== 'ray') return false;
  return (
    debug.sampleY != null &&
    Number.isFinite(debug.sampleY) &&
    Math.abs(debug.sampleY) <= MAX_ABS_FLOOR_Y
  );
}

/**
 * Best-effort Y when the quality lock is slow (soft timeout).
 * Prefer ray → mesh-AABB. Returns null if tiles are still empty — keep waiting.
 */
export function bestEffortFloorY(debug = lastDebug) {
  if ((debug?.meshCount || 0) < 1) return null;
  if (
    debug?.sampleY != null &&
    Number.isFinite(debug.sampleY) &&
    Math.abs(debug.sampleY) <= MAX_ABS_FLOOR_Y &&
    (debug.source === 'ray' || debug.source === 'mesh-aabb')
  ) {
    return debug.sampleY;
  }
  if (
    cachedOriginFloorY != null &&
    Number.isFinite(cachedOriginFloorY) &&
    Math.abs(cachedOriginFloorY) <= MAX_ABS_FLOOR_Y
  ) {
    return cachedOriginFloorY;
  }
  return null;
}

/** True when soft-timeout may force a lock (never on empty tiles). */
export function canForceFloorLock(debug = lastDebug) {
  return bestEffortFloorY(debug) != null;
}

/** Lot centroid in local XZ (meters). Falls back to geocode pin / origin. */
export function lotCentroidXZ(vertices) {
  if (!vertices?.length) return [0, 0];
  let sx = 0;
  let sz = 0;
  for (const [x, z] of vertices) {
    sx += Number(x) || 0;
    sz += Number(z) || 0;
  }
  const n = vertices.length;
  return [sx / n, sz / n];
}

/**
 * Fixed iso framing aimed at the lot (or pin if no verts).
 * Docs: docs/ADU_SITE_PLANNER.md — follow lot, not scene origin.
 */
export function applyFixedLoadCamera(controls, floorY = 0, cx = 0, cz = 0) {
  if (!controls?.setLookAt) return;
  const y = Number(floorY) || 0;
  const x = Number(cx) || 0;
  const z = Number(cz) || 0;
  const [ex, ey, ez] = FIXED_LOAD_EYE;
  controls.setLookAt(ex + x, ey + y, ez + z, x, y, z, true);
}

export function getCachedOriginFloorY() {
  return cachedOriginFloorY;
}

export function bumpFloorSettleGeneration() {
  settleGeneration += 1;
  return settleGeneration;
}

export function getFloorSettleGeneration() {
  return settleGeneration;
}

export function clearFloorCache() {
  cachedOriginFloorY = null;
  lastDebug = {
    meshCount: 0,
    sampleY: null,
    source: 'none',
    at: [0, 0],
  };
}

function isPlausibleFloorY(y) {
  return y != null && Number.isFinite(y) && Math.abs(y) <= MAX_ABS_FLOOR_Y;
}

function isGroundPickable(obj) {
  if (!obj?.visible || obj.userData?.lotUi) return false;
  if (
    obj.isLine ||
    obj.isLineSegments ||
    obj.isLine2 ||
    obj.isLineSegments2 ||
    obj.type === 'Line' ||
    obj.type === 'Line2' ||
    obj.type === 'LineSegments2'
  ) {
    return false;
  }
  return Boolean(obj.isMesh || obj.isInstancedMesh || obj.isBatchedMesh);
}

/** Prefer the live tiles group from the registry; fall back to scene walk. */
export function collectTileMeshes(scene) {
  const out = [];
  const root = getPhotorealisticTilesGroup() || null;

  const pushPickable = (obj) => {
    if (isGroundPickable(obj)) out.push(obj);
  };

  if (root) {
    root.updateWorldMatrix(true, true);
    root.traverse(pushPickable);
    if (out.length) return out;
  }

  if (!scene) return out;
  scene.traverse((obj) => {
    if (!isGroundPickable(obj)) return;
    let p = obj;
    let isTile = false;
    while (p) {
      if (p.userData?.photorealisticTiles) {
        isTile = true;
        break;
      }
      p = p.parent;
    }
    if (isTile) out.push(obj);
  });
  return out;
}

function pickHitY(hits) {
  if (!hits?.length) return null;
  // Prefer the *lowest* upward-facing hit along the ray so a probe that
  // grazes a roof/tree can still find grade below when present.
  let best = null;
  const limit = Math.min(hits.length, 24);
  for (let i = 0; i < limit; i++) {
    const h = hits[i];
    let ny = 1;
    if (h.face?.normal) {
      ny = h.face.normal.clone().transformDirection(h.object.matrixWorld).y;
    }
    if (ny < 0.05) continue;
    if (!isPlausibleFloorY(h.point.y)) continue;
    if (best == null || h.point.y < best) best = h.point.y;
  }
  if (best != null) return best;
  for (const h of hits) {
    if (isPlausibleFloorY(h.point.y)) return h.point.y;
  }
  return null;
}

/**
 * Downward ray at (x,z). Returns world Y of the first plausible hit, or null.
 */
export function sampleTileSurfaceY(scene, x, z, camera = null) {
  const tiles = getPhotorealisticTilesGroup();
  if (camera) _raycaster.camera = camera;
  _raycaster.near = 0;
  _raycaster.far = 1e5;

  // Two start heights — extreme rays can miss thin LODs / precision issues.
  const starts = [2500, 400];
  for (const startY of starts) {
    _origin.set(x, startY, z);
    _raycaster.set(_origin, _down);

    if (tiles) {
      tiles.updateWorldMatrix(true, true);
      const y = pickHitY(_raycaster.intersectObject(tiles, true));
      if (y != null) return y;
    }

    const targets = collectTileMeshes(scene);
    if (targets.length) {
      const y = pickHitY(_raycaster.intersectObjects(targets, false));
      if (y != null) return y;
    }
  }

  return null;
}

/** Lowest plausible sample — prefers yard/grade over rooftops at the geocode pin. */
function lowest(values) {
  if (!values.length) return null;
  return Math.min(...values);
}

export function sampleFloorY(scene, probes, camera = null) {
  const ys = [];
  for (const [x, z] of probes) {
    const y = sampleTileSurfaceY(scene, x, z, camera);
    if (isPlausibleFloorY(y)) ys.push(y);
  }
  return lowest(ys);
}

/**
 * Fallback: median of nearby tile-mesh AABB tops (local, plausible only).
 * Never use the whole tileset AABB — before LODs load it can be ECEF-scale.
 */
function nearbyMeshTopFallback(scene, x, z, radius = 60) {
  const meshes = collectTileMeshes(scene);
  const tops = [];
  for (const m of meshes) {
    _box.setFromObject(m);
    if (_box.isEmpty()) continue;
    _box.getCenter(_center);
    const dx = _center.x - x;
    const dz = _center.z - z;
    if (dx * dx + dz * dz > radius * radius) continue;
    const top = _box.max.y;
    if (isPlausibleFloorY(top)) tops.push(top);
  }
  // Prefer lower tops (terrain) over building roofs when AABB is all we have.
  return lowest(tops);
}

function finishSample(y, source, meshes, at) {
  lastDebug = {
    meshCount: meshes.length,
    sampleY: y,
    source,
    at,
  };
  if (isPlausibleFloorY(y)) cachedOriginFloorY = y;
  return isPlausibleFloorY(y) ? y : null;
}

/**
 * Cross + outer-ring probes around origin. Prefer the ring first — geocode
 * pins often land on a roof; yard/street grade shows up off-center once LODs
 * fill in. Keep the list small — it runs while waiting to lock.
 */
export function sampleOriginFloorY(scene, camera = null) {
  const ringProbes = [
    [8, 0],
    [-8, 0],
    [0, 8],
    [0, -8],
    [16, 0],
    [-16, 0],
    [0, 16],
    [0, -16],
    [22, 22],
    [22, -22],
    [-22, 22],
    [-22, -22],
    [30, 0],
    [-30, 0],
    [0, 30],
    [0, -30],
  ];
  const meshes = collectTileMeshes(scene);
  // Outer ring first (yard/street), then pin as last resort.
  let y = sampleFloorY(scene, ringProbes, camera);
  if (y == null) y = sampleFloorY(scene, [[0, 0]], camera);
  if (y != null) return finishSample(y, 'ray', meshes, [0, 0]);

  y = nearbyMeshTopFallback(scene, 0, 0);
  if (y != null) return finishSample(y, 'mesh-aabb', meshes, [0, 0]);

  return finishSample(null, meshes.length ? 'no-hit' : 'no-meshes', meshes, [
    0, 0,
  ]);
}

/** Lot footprint probes (centroid + vertices). */
export function sampleLotFloorY(scene, vertices, camera = null) {
  if (!vertices?.length) return sampleOriginFloorY(scene, camera);
  const n = vertices.length;
  const cx = vertices.reduce((s, v) => s + v[0], 0) / n;
  const cz = vertices.reduce((s, v) => s + v[1], 0) / n;
  const meshes = collectTileMeshes(scene);
  let y = sampleFloorY(scene, [[cx, cz], ...vertices], camera);
  if (y != null) return finishSample(y, 'ray', meshes, [cx, cz]);

  y = nearbyMeshTopFallback(scene, cx, cz);
  if (y != null) return finishSample(y, 'mesh-aabb', meshes, [cx, cz]);

  return finishSample(null, meshes.length ? 'no-hit' : 'no-meshes', meshes, [
    cx,
    cz,
  ]);
}

/**
 * Convert a desired world Y into local Y under `object3d`'s parent.
 */
export function worldYToLocal(object3d, worldY) {
  if (!object3d?.parent) return worldY;
  object3d.parent.updateWorldMatrix(true, false);
  return worldY - object3d.parent.getWorldPosition(_parentWorld).y;
}

/**
 * Target local Y for the lot: sample tile floor under the footprint, convert
 * to parent-local, add bias + manual elevation.
 */
export function computeLotLocalY({
  scene,
  camera,
  object3d,
  vertices,
  elevation = 0,
  seatToTiles = true,
}) {
  const elev = Number(elevation) || 0;
  if (!seatToTiles) {
    return 0.15 + elev;
  }
  const worldFloor = sampleLotFloorY(scene, vertices, camera);
  if (!isPlausibleFloorY(worldFloor)) {
    const cached = getCachedOriginFloorY();
    if (isPlausibleFloorY(cached) && object3d) {
      return worldYToLocal(object3d, cached) + LOT_SURFACE_BIAS + elev;
    }
    // Stay at local bias until tiles give a real sample — never jump to ECEF.
    return LOT_SURFACE_BIAS + elev;
  }
  const localFloor = object3d
    ? worldYToLocal(object3d, worldFloor)
    : worldFloor;
  return localFloor + LOT_SURFACE_BIAS + elev;
}
